import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import { TopBar } from "../components/TopBar";
import { RestTimer } from "../components/RestTimer";
import { api, ApiError } from "../api/client";
import type { ExerciseHistoryEntry, SessionExercise, SetResult, WorkoutSession } from "../api/types";
import { useExerciseMap } from "../hooks/useExerciseLibrary";
import { enqueueSessionUpdate } from "../state/offline";

const SAVE_DEBOUNCE_MS = 700;

const inc = (v: number | undefined, by: number, min = 0) =>
  Math.max(min, Math.round((v ?? 0) + by));

const Step = ({
  value,
  onChange,
  step,
}: {
  value: number | undefined;
  onChange: (v: number) => void;
  step: number;
}) => (
  <>
    <button className="btn small secondary" onClick={() => onChange(inc(value, -step))} aria-label="dec">
      −
    </button>
    <button className="btn small secondary" onClick={() => onChange(inc(value, step))} aria-label="inc">
      +
    </button>
  </>
);

export const Session = () => {
  const { id = "" } = useParams();
  const nav = useNavigate();
  const { map: exMap } = useExerciseMap();
  const [session, setSession] = useState<WorkoutSession | null>(null);
  const [previous, setPrevious] = useState<Record<string, ExerciseHistoryEntry | null>>({});
  const [active, setActive] = useState(0);
  const [saving, setSaving] = useState(false);
  const [confirmFinish, setConfirmFinish] = useState(false);
  const dirty = useRef(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    api<WorkoutSession>(`/sessions/${id}`).then(setSession).catch(() => {});
  }, [id]);

  // Preload "last performance" for each exercise so the user can chase progressive overload
  useEffect(() => {
    if (!session) return;
    const ids = Array.from(new Set(session.exercises.map((e) => e.exerciseId)));
    Promise.all(
      ids.map((eid) =>
        api<ExerciseHistoryEntry | null>(`/exercises/${eid}/last`).then((p) => [eid, p] as const).catch(() => [eid, null] as const),
      ),
    ).then((entries) => setPrevious(Object.fromEntries(entries)));
  }, [session?.id]);

  const scheduleSave = useCallback(
    (s: WorkoutSession) => {
      dirty.current = true;
      if (timer.current) clearTimeout(timer.current);
      timer.current = setTimeout(async () => {
        try {
          setSaving(true);
          await api(`/sessions/${id}`, { method: "PUT", body: s });
          dirty.current = false;
        } catch (err) {
          if (err instanceof ApiError && err.status !== 401) {
            await enqueueSessionUpdate(id, s);
          }
        } finally {
          setSaving(false);
        }
      }, SAVE_DEBOUNCE_MS);
    },
    [id],
  );

  const update = (mutator: (s: WorkoutSession) => WorkoutSession) => {
    setSession((prev) => {
      if (!prev) return prev;
      const next = mutator(prev);
      scheduleSave(next);
      return next;
    });
  };

  // An exercise is "unfinished" if it has no set marked as completed.
  const unfinishedIndexes = useMemo(() => {
    if (!session) return [];
    return session.exercises
      .map((e, i) => ({ e, i }))
      .filter(({ e }) => !(e.sets ?? []).some((s) => s.completed))
      .map(({ i }) => i);
  }, [session]);

  const finish = async () => {
    if (!session) return;
    if (timer.current) clearTimeout(timer.current);
    setConfirmFinish(false);
    await api(`/sessions/${id}`, { method: "PUT", body: session });
    await api(`/sessions/${id}/finish`, { method: "POST" });
    nav("/history", { replace: true });
  };

  const attemptFinish = () => {
    if (unfinishedIndexes.length > 0) {
      setConfirmFinish(true);
      return;
    }
    void finish();
  };

  const cur = session?.exercises[active];
  const curLib = cur ? exMap.get(cur.exerciseId) : undefined;
  const curPrev = cur ? previous[cur.exerciseId] : null;

  const setSet = (i: number, patch: Partial<SetResult>) =>
    update((s) => ({
      ...s,
      exercises: s.exercises.map((e, idx) =>
        idx !== active
          ? e
          : { ...e, sets: e.sets.map((st, si) => (si === i ? { ...st, ...patch } : st)) },
      ),
    }));

  const addSet = () =>
    update((s) => ({
      ...s,
      exercises: s.exercises.map((e, idx) => {
        if (idx !== active) return e;
        const last = e.sets[e.sets.length - 1];
        const setNumber = (last?.setNumber ?? 0) + 1;
        return {
          ...e,
          sets: [
            ...e.sets,
            { setNumber, weight: last?.weight, reps: last?.reps, completed: false },
          ],
        };
      }),
    }));

  const removeSet = (i: number) =>
    update((s) => ({
      ...s,
      exercises: s.exercises.map((e, idx) =>
        idx !== active ? e : { ...e, sets: e.sets.filter((_, si) => si !== i) },
      ),
    }));

  const next = () =>
    setActive((a) => Math.min(a + 1, (session?.exercises.length ?? 1) - 1));
  const prev = () => setActive((a) => Math.max(a - 1, 0));

  const isPR = useMemo(() => {
    if (!cur || !curPrev) return false;
    const bestThis = (cur.sets ?? [])
      .filter((s) => s.completed)
      .reduce((acc, s) => Math.max(acc, (s.weight ?? 0) * (s.reps ?? 0)), 0);
    const bestPrev = (curPrev.bestSet?.weight ?? 0) * (curPrev.bestSet?.reps ?? 0);
    return bestThis > bestPrev && bestThis > 0;
  }, [cur, curPrev]);

  if (!session) return <div className="page center">Loading…</div>;
  if (!cur)
    return (
      <div className="page">
        <TopBar title={session.name ?? "Workout"} back />
        <div className="muted">No exercises in this session.</div>
        <button className="btn" onClick={() => void finish()}>
          Finish
        </button>
      </div>
    );

  return (
    <div className="page" style={{ paddingBottom: 100 }}>
      <TopBar
        title={session.name ?? "Workout"}
        back
        right={
          <span className="muted" style={{ fontSize: 12 }}>{saving ? "Saving…" : "Saved"}</span>
        }
      />

      <RestTimer />

      <div className="row" style={{ marginTop: 16, marginBottom: 8 }}>
        <div className="muted">
          Exercise {active + 1} of {session.exercises.length}
        </div>
        <div className="spacer" />
        {isPR && <span className="chip solid">PR!</span>}
      </div>

      <div className="exercise-header">
        <div className="thumb">{(curLib?.name ?? "•").charAt(0)}</div>
        <div style={{ flex: 1 }}>
          <h2 className="h2" style={{ margin: 0 }}>
            {curLib?.name ?? cur.exerciseId}
          </h2>
          {curLib && (
            <div className="muted">
              {curLib.muscleGroup} · {curLib.equipment}
            </div>
          )}
        </div>
      </div>

      {curPrev ? (
        <div className="previous-line">
          <strong>Last time:</strong>{" "}
          {curPrev.sets
            .map((s) => `${s.weight ?? "-"} × ${s.reps ?? "-"}`)
            .join("   ·   ")}
        </div>
      ) : (
        <div className="previous-line">No previous data yet.</div>
      )}

      <div className="col" style={{ marginTop: 12 }}>
        <div className="set-row" style={{ color: "var(--ink-dim)", fontSize: 12, fontWeight: 600 }}>
          <div>#</div><div>Weight</div><div>Reps</div><div>Done</div><div></div>
        </div>
        {cur.sets.map((s, i) => (
          <div key={i} className="set-row">
            <div className="num">{s.setNumber}</div>
            <input
              inputMode="decimal"
              value={s.weight ?? ""}
              onChange={(e) => setSet(i, { weight: e.target.value === "" ? undefined : Number(e.target.value) })}
            />
            <input
              inputMode="numeric"
              value={s.reps ?? ""}
              onChange={(e) => setSet(i, { reps: e.target.value === "" ? undefined : Number(e.target.value) })}
            />
            <input
              type="checkbox"
              className="done"
              checked={!!s.completed}
              onChange={(e) => setSet(i, { completed: e.target.checked })}
            />
            <button className="rm" onClick={() => removeSet(i)} aria-label="remove set">×</button>
          </div>
        ))}
      </div>

      <div className="row" style={{ marginTop: 6, gap: 8 }}>
        <Step value={cur.sets[cur.sets.length - 1]?.weight} step={5} onChange={(v) => setSet(cur.sets.length - 1, { weight: v })} />
        <span className="muted" style={{ fontSize: 12 }}>weight ±5</span>
        <div className="spacer" />
        <Step value={cur.sets[cur.sets.length - 1]?.reps} step={1} onChange={(v) => setSet(cur.sets.length - 1, { reps: v })} />
        <span className="muted" style={{ fontSize: 12 }}>reps ±1</span>
      </div>

      <button className="btn secondary" style={{ marginTop: 12 }} onClick={addSet}>
        + Add set
      </button>

      <label className="field" style={{ marginTop: 16 }}>
        Exercise notes
        <textarea
          rows={2}
          value={cur.notes ?? ""}
          onChange={(e) =>
            update((s) => ({
              ...s,
              exercises: s.exercises.map((ex, idx) => (idx === active ? { ...ex, notes: e.target.value } : ex)),
            }))
          }
        />
      </label>

      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          background: "white",
          borderTop: "1px solid var(--line)",
          padding: "10px 16px",
          paddingBottom: "calc(10px + env(safe-area-inset-bottom))",
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gap: 8,
          zIndex: 12,
        }}
      >
        <button className="btn secondary" onClick={prev} disabled={active === 0}>
          Prev
        </button>
        {active === session.exercises.length - 1 ? (
          <button className="btn" onClick={attemptFinish}>
            Finish
          </button>
        ) : (
          <button className="btn" onClick={next}>
            Next
          </button>
        )}
      </div>

      {confirmFinish && (
        <div
          role="dialog"
          aria-modal="true"
          onClick={() => setConfirmFinish(false)}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "flex-end",
            justifyContent: "center",
            zIndex: 20,
          }}
        >
          <div
            className="card"
            onClick={(e) => e.stopPropagation()}
            style={{
              width: "100%",
              maxWidth: 520,
              margin: 16,
              marginBottom: "calc(16px + env(safe-area-inset-bottom))",
            }}
          >
            <h2 className="h2" style={{ marginTop: 0 }}>
              Unfinished {unfinishedIndexes.length === 1 ? "exercise" : "exercises"}
            </h2>
            <div className="muted">
              You haven't completed any sets for:
            </div>
            <ul style={{ margin: "8px 0 12px", paddingLeft: 18 }}>
              {unfinishedIndexes.map((i) => (
                <li key={i}>
                  {exMap.get(session.exercises[i].exerciseId)?.name ?? session.exercises[i].exerciseId}
                </li>
              ))}
            </ul>
            <div className="col" style={{ gap: 8 }}>
              <button
                className="btn"
                onClick={() => {
                  setActive(unfinishedIndexes[0]);
                  setConfirmFinish(false);
                }}
              >
                Go to exercise
              </button>
              <button className="btn secondary" onClick={() => void finish()}>
                Finish anyway
              </button>
              <button className="btn secondary" onClick={() => setConfirmFinish(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
